import { createContext, useState, useEffect, useMemo } from 'react';
import { trackPageView } from '../utils/analytics';
import { 
  getCookiePreferences, 
  setLanguageCookie, 
  getLanguageCookie,
  isCookieTypeEnabled
} from '../utils/cookie';

// Create the context
export const LanguageContext = createContext();

// Create the provider component
const LanguageProvider = ({ children }) => {
  // Initialize language from cookie if present, otherwise default to Thai
  const getInitialLanguage = () => {
    try {
      if (typeof document === 'undefined') return 'th';
      // Only load language from cookie if user has accepted/configured cookies
      const prefs = getCookiePreferences();
      if (!prefs || !prefs.functional) return 'th'; // Functional cookies not enabled
      
      const languageFromCookie = getLanguageCookie();
      return languageFromCookie || 'th';
    } catch (e) {
      return 'th';
    }
  };

  const [language, setLanguageState] = useState(getInitialLanguage);

  // Public setter that persists selection to cookie (only if functional cookies are enabled)
  const setLanguage = (lang) => {
    setLanguageState(lang);
    try {
      if (isCookieTypeEnabled('functional')) {
        setLanguageCookie(lang);
      }
    } catch (e) {
      // ignore
    }
  };


  const currentYearEn = new Date().getFullYear();
  const currentYearTh = currentYearEn + 543;

  const textContent = useMemo(() => ({
    th: {
        header: "บริษัท เอไอ บิสซิเนส เทคโนโลยี จำกัด",
        heroTitle: "ยินดีต้อนรับสู่ บริษัท เอไอ บิสซิเนส เทคโนโลยี จำกัด",
        heroDescription: "เรานำเสนอโซลูชัน AI ที่ล้ำสมัยเพื่อช่วยให้ธุรกิจของคุณเติบโตในยุคดิจิทัล",
        exploreServices: "สำรวจบริการของเรา",
        aboutTitle: "เกี่ยวกับเรา",
        servicesTitle: "บริการของเรา",
        contactTitle: "ติดต่อเรา",
        address: "ที่อยู่ของเรา",
        sendMessage: "ส่งข้อความ",
        messageSent: "เราได้รับข้อความของคุณเรียบร้อยแล้ว!",
        messageFailed: "การส่งข้อความล้มเหลว กรุณาลองอีกครั้ง",
        errorOccurred: "เกิดข้อผิดพลาดขณะส่งข้อความ",
        location: "ตำแหน่งของเรา",
        companyName: "บริษัท เอไอ บิสซิเนส เทคโนโลยี จำกัด",
        companyAddress: "เลขที่ 5/443 ถ.ประชาชื่น",
        companyDistrict: "แขวงบางซื่อ เขตบางซื่อ",
        companyCity: "กรุงเทพมหานคร 10800",
        emailAddress: "info@aibusiness.co.th",
        phone: "โทรศัพท์",
        phoneNumber: "061-253-0020",
        map: "ดูแผนที่",
        name: "ชื่อ",
        email: "อีเมล",
        message: "ข้อความ",
        followUs: "ติดตามเรา",
        allRightsReserved: `©สงวนลิขสิทธิ์ ${currentYearTh} บริษัท เอไอ บิสซิเนส เทคโนโลยี จำกัด`,
        PrivacyPolicy: "นโยบายความเป็นส่วนตัว",
        CookiePolicy: "นโยบายการใช้คุกกี้",
        companyContact: "บริษัท เอไอ บิสซิเนส เทคโนโลยี จำกัด\nอีเมล: info@aibusiness.co.th\nโทรศัพท์: 061-253-0020",
        cookieConsentMsg: "เว็บไซต์นี้ใช้คุกกี้เพื่อปรับปรุงประสบการณ์ของคุณ อ่านเพิ่มเติมได้ที่",
        accept: "ยอมรับ",
        reject: "ปฏิเสธ",
        settings: "ตั้งค่า",
        confirmSettings: "ยืนยันการตั้งค่า",
        close: "ปิด",
        cookiePreferences: "ตั้งค่าคุกกี้",
        necessaryCookies: "คุกกี้จำเป็น",
        necessaryCookiesDesc: "เพื่อจัดเก็บข้อมูลที่จำเป็นต่อการทำงานของเว็บไซต์ ได้แก่ การยอมรับนโยบายการใช้งานคุกกี้",
        functionalCookies: "คุกกี้การทำงาน",
        functionalCookiesDesc: "เพื่อจดจำการตั้งค่าของผู้ใช้ ได้แก่ ภาษาที่แสดงบนเว็บไซต์",
        analyticsCookies: "คุกกี้วิเคราะห์",
        analyticsCookiesDesc: "เพื่อช่วยวิเคราะห์พฤติกรรมการใช้งานเว็บไซต์",
    },
    en: {
        header: "AI Business Technology Co., Ltd.",
        heroTitle: "Welcome to AI Business Technology Co., Ltd.",
        heroDescription: "We provide cutting-edge AI solutions to help your business thrive in the digital age.",
        exploreServices: "Explore Our Services",
        aboutTitle: "About Us",
        servicesTitle: "Our Services",
        contactTitle: "Contact Us",
        address: "Our Address",
        sendMessage: "Send Message",
        messageSent: "We have received your message!",
        messageFailed: "Failed to send message. Try again.",
        errorOccurred: "An error occurred while sending the message.",
        location: "Our Location",
        companyName: "AI Business Technology Co., Ltd.",
        companyAddress: "5/443 Prachachuen Road",
        companyDistrict: "Bang Sue Subdistrict, Bang Sue District",
        companyCity: "Bangkok 10800",
        emailAddress: "info@aibusiness.co.th",
        phone: "Phone",
        phoneNumber: "061-253-0020",        
        map: "View Map",
        name: "Name",
        email: "Email",
        message: "Message",
        followUs: "Follow Us",
        allRightsReserved: `© ${currentYearEn} AI Business Technology Co., Ltd. All rights reserved.`,
        PrivacyPolicy: "Privacy Policy",
        CookiePolicy: "Cookie Policy",
        companyContact: "AI Business Technology Co., Ltd.\nEmail: info@aibusiness.co.th\nPhone: 061-253-0020",
        cookieConsentMsg: "This website uses cookies to improve your experience. Read more at",
        accept: "Accept",
        reject: "Reject",
        settings: "Settings",
        confirmSettings: "Confirm Settings",
        close: "Close",
        cookiePreferences: "Cookie Preferences",
        necessaryCookies: "Necessary Cookies",
        necessaryCookiesDesc: "To store information necessary for the functioning of the website, i.e., the acceptance of the cookie policy.",
        functionalCookies: "Functional Cookies",
        functionalCookiesDesc: "To remember user settings, i.e., website language.",
        analyticsCookies: "Analytics Cookies",
        analyticsCookiesDesc: "To analyze website usage behavior.",
    },
  }), [currentYearEn, currentYearTh]);

    // Keep document.title in sync with selected language's company name
  useEffect(() => {
    try {
      const company = textContent[language] && textContent[language].companyName;
      if (!company) return;

      // If the user is currently on a tracked hash section, update the full title
      const hash = (window.location.hash || '').replace('#', '');
      const TRACKED = ['hero', 'about', 'services', 'contact'];
      if (hash && TRACKED.includes(hash)) {
        const labels = {
          th: {
            hero: 'หน้าแรก',
            about: textContent.th.aboutTitle,
            services: textContent.th.servicesTitle,
            contact: textContent.th.contactTitle,
          },
          en: {
            hero: 'Home',
            about: textContent.en.aboutTitle,
            services: textContent.en.servicesTitle,
            contact: textContent.en.contactTitle,
          },
        };
        const title = `${company} | ${labels[language][hash]}`;
        document.title = title;
        // update GA config for this path/title (no-op in dev)
        try {
          trackPageView(hash, title);
        } catch (e) {
          // ignore
        }
        return;
      }

      // Default: just set company name as document.title
      document.title = company;
    } catch (e) {
      // ignore
    }
  }, [language, textContent]);

  // Ensure cookie remains in sync if language changes by any other means
  useEffect(() => {
    try {
      if (isCookieTypeEnabled('functional')) {
        setLanguageCookie(language);
      }
    } catch (e) {
      // ignore
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, textContent: textContent[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
